/**
 * Pantalla de acceso.
 *
 * Un formulario, dos campos y ningun adorno. Se apoya en el `<form>` nativo para
 * que Enter envie, para que el gestor de contrasenas del navegador reconozca los
 * campos (`autocomplete`) y para que el envio funcione sin JavaScript de teclado
 * propio.
 */

import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

import { IconAlert } from '@/components/shell/icons';
import { Button } from '@/components/shell/primitives';
import { ThemeToggle } from '@/components/shell/ThemeToggle';
import { useSessionStore } from '@/store/session';

export function LoginScreen() {
  const signIn = useSessionStore((estado) => estado.signIn);
  const signingIn = useSessionStore((estado) => estado.signingIn);
  const error = useSessionStore((estado) => estado.error);

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const enviar = (evento: FormEvent<HTMLFormElement>) => {
    evento.preventDefault();
    if (username.trim() === '' || password === '') return;
    void signIn(username.trim(), password);
  };

  return (
    <div className="grid min-h-svh lg:grid-cols-[1.1fr_1fr]">
      {/* Panel de marca: solo en pantallas anchas, donde hay lugar de sobra. */}
      <aside className="relative hidden flex-col justify-between bg-app-subtle p-12 lg:flex">
        <div className="flex items-center gap-3">
          <span
            aria-hidden="true"
            className="grid h-10 w-10 place-items-center rounded-xl bg-accent text-lg font-bold text-accent-contrast"
          >
            N
          </span>
          <span className="text-lg font-semibold tracking-tight">Nucleo</span>
        </div>

        <div className="max-w-md">
          <h1 className="text-3xl font-semibold leading-tight tracking-tight">
            El ERP que habla el idioma de tu oficio.
          </h1>
          {/* La bajada nombra el troncal generico, no el vertical: administracion
              de consorcios es un modulo mas (ARQUITECTURA.md §8) y esta pantalla
              la ve tambien quien entra a facturar o a comprar. */}
          <p className="mt-4 text-fg-muted">
            Ventas, compras, stock, contabilidad y facturación. Cada empresa con sus libros, sin
            duplicar trabajo.
          </p>
        </div>

        <p className="text-sm text-fg-faint">Nucleo ERP · versión 0.1</p>
      </aside>

      <main className="relative flex flex-col justify-center px-6 py-12 sm:px-12">
        <div className="absolute right-4 top-4">
          <ThemeToggle />
        </div>

        <div className="mx-auto w-full max-w-sm">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <span
              aria-hidden="true"
              className="grid h-10 w-10 place-items-center rounded-xl bg-accent text-lg font-bold text-accent-contrast"
            >
              N
            </span>
            <span className="text-lg font-semibold tracking-tight">Nucleo</span>
          </div>

          <h2 className="text-2xl font-semibold tracking-tight">Ingresar</h2>
          <p className="mt-1 text-fg-muted">Usá las credenciales de tu organización.</p>

          {error !== null && (
            <div
              role="alert"
              className="mt-6 flex items-start gap-2.5 rounded-lg border border-danger/30 bg-danger-soft p-3 text-sm"
            >
              <IconAlert className="h-5 w-5 shrink-0 text-danger" />
              <p>{error}</p>
            </div>
          )}

          <form onSubmit={enviar} className="mt-6 space-y-4">
            <div>
              {/* El identificador de acceso es el email: `base.User` no tiene
                  campo `username`. Etiquetarlo "Usuario" haría que alguien
                  probara un nombre corto y no pudiera entrar nunca. */}
              <label htmlFor="login-usuario" className="mb-1.5 block text-sm font-medium">
                Email
              </label>
              <input
                id="login-usuario"
                name="email"
                type="email"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
                value={username}
                onChange={(evento) => {
                  setUsername(evento.target.value);
                }}
                className="h-11 w-full rounded-md border border-line bg-surface px-3 outline-none transition-colors focus:border-accent"
              />
            </div>

            <div>
              <div className="mb-1.5 flex items-baseline justify-between gap-2">
                <label htmlFor="login-clave" className="block text-sm font-medium">
                  Contraseña
                </label>
                {/* Va acá y no al pie: quien no puede entrar mira el campo que
                    le falla, no el final de la pantalla. */}
                <Link to="/recuperar" className="text-xs text-accent hover:underline">
                  ¿La olvidaste?
                </Link>
              </div>
              <input
                id="login-clave"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(evento) => {
                  setPassword(evento.target.value);
                }}
                className="h-11 w-full rounded-md border border-line bg-surface px-3 outline-none transition-colors focus:border-accent"
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              loading={signingIn}
              className="h-11 w-full"
              disabled={username.trim() === '' || password === ''}
            >
              {signingIn ? 'Ingresando…' : 'Ingresar'}
            </Button>
          </form>

          <p className="mt-8 text-xs text-fg-faint">
            ¿Te invitaron y todavía no elegiste tu contraseña? Abrí el enlace que te mandaron. Si no
            lo tenés, pedile a quien administra el sistema que te genere uno nuevo.
          </p>
        </div>
      </main>
    </div>
  );
}
